import { useEffect } from 'react';
import { useAppStore } from '@/stores/appStore';
import { eventBus } from '@/map/core/events';
import { SCENARIO_REGISTRY } from '@/map/scenario/definitions';

export function useKeyboardControls() {
  const closePanel = useAppStore((s) => s.closePanel);
  const reset = useAppStore((s) => s.reset);
  const setTimeOfDay = useAppStore((s) => s.setTimeOfDay);
  const setBreadcrumb = useAppStore((s) => s.setBreadcrumb);
  const triggerScenario = useAppStore((s) => s.triggerScenario);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      // ── QA seek/jump harness (Phase 0.7) ────────────────────────────
      // Shift+1..9 → quick-launch scenario N from the registry (skips the
      // picker so a reviewer can jump straight between scenarios).
      // Shift+Backspace → stop the active scenario.
      if (e.shiftKey && /^[1-9]$/.test(e.key)) {
        const idx = parseInt(e.key, 10) - 1;
        const meta = SCENARIO_REGISTRY[idx];
        if (meta) {
          e.preventDefault();
          triggerScenario(meta.id);
        }
        return;
      }
      if (e.shiftKey && e.key === 'Backspace') {
        e.preventDefault();
        eventBus.emit('timeline:command', { action: 'stop' });
        return;
      }

      switch (e.key) {
        // Number keys for camera presets
        case '1':
          setBreadcrumb(['North Vale', 'Overview']);
          break;
        case '2':
          setBreadcrumb(['North Vale', 'Town Centre']);
          break;
        case '3':
          setBreadcrumb(['North Vale', 'Residential']);
          break;
        case '4':
          setBreadcrumb(['North Vale', 'Depot']);
          break;
        case '5':
          setBreadcrumb(['North Vale', 'Control Tower']);
          break;
        case '6':
          setBreadcrumb(['North Vale', 'Water Network']);
          break;
        case '7':
          setBreadcrumb(['North Vale', 'Gas Network']);
          break;
        case '8':
          setBreadcrumb(['North Vale', 'Electricity']);
          break;
        case '9':
          setBreadcrumb(['North Vale', 'Wastewater']);
          break;

        // Space toggles scenario play/pause (ignored when no scenario active)
        case ' ':
          e.preventDefault();
          eventBus.emit('timeline:command', { action: 'toggle' });
          break;

        // Escape to close panel
        case 'Escape':
          closePanel();
          break;

        // R for reset
        case 'r':
        case 'R':
          if (!e.ctrlKey && !e.metaKey) {
            reset();
          }
          break;

        // Time of day controls
        case '[':
          setTimeOfDay(useAppStore.getState().worldState.timeOfDay - 0.05);
          break;
        case ']':
          setTimeOfDay(useAppStore.getState().worldState.timeOfDay + 0.05);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closePanel, reset, setTimeOfDay, setBreadcrumb, triggerScenario]);
}
